import type { SensorUomResponse } from '../../Services/serviceURL';

export interface AssetParameter {
  tag: string;
  sensorName: string;
  isUserConfigurable: boolean;
  units: {
    code: string;
    description: string;
  }[];
  channelId?: string;
  position?: string;
  hasUom: boolean;
}

export interface AssetType {
  typeName: string;
  allowSubAssets: boolean;
  controllerType?: string;
  isUserConfigurable: boolean;
  parameters: AssetParameter[];
}

export interface UIParameter {
  tag: string;
  sensor: string;
  position: string;
  businessFriendlyName: string;
  value: string;
  units: string;
  unitOptions: {
    code: string;
    description: string;
  }[];
  originalUnits: {
    code: string;
    description: string;
  }[];
  isUserConfigurable?: boolean;
  selectedSensorType?: string;
  hasUom: boolean;
}

export interface PlantData {
  customerId: string;
  customerName: string;
  siteId: string;
  siteName: string;
  plantName: string;
  plantStatus: string;
  assets: unknown[];
}

export interface AssetDetails {
  id: string;
  type: string;
  name: string;
  status: string;
  parameters: {
    tag: string;
    sensorName: string;
    position: string;
    displayName: string;
    unit?: string;
  }[];
  gatewayDeviceType?: string;
  gatewayDeviceId?: string;
  simNumber?: string;
  controllerType?: string;
  controllerDeviceId?: string;
}

export interface OriginalAssetFormSnapshot {
  selectedAsset: string;
  assetName: string;
  assetStatus: string;
  gatewayDeviceType: string;
  gatewayDeviceId: string;
  simNumber: string;
  controllerType: string;
  controllerDeviceId: string;
  parameters: UIParameter[];
}

export function mergeAllUomOptions(
  validUoms: SensorUomResponse[]
): UIParameter['unitOptions'] {
  const allUomsMap = new Map<string, { code: string; description: string }>();
  for (const sensorUom of validUoms) {
    for (const uom of sensorUom.uoms) {
      allUomsMap.set(uom.code, uom);
    }
  }
  return Array.from(allUomsMap.values());
}

export function applyBusinessFriendlyNameChange(
  parameters: UIParameter[],
  index: number,
  businessFriendlyName: string,
  param: UIParameter,
  validUoms: SensorUomResponse[]
): UIParameter[] {
  const updated = [...parameters];
  const row: UIParameter = { ...updated[index], businessFriendlyName };

  if (param.isUserConfigurable === true && validUoms.length > 0) {
    const inputValue = businessFriendlyName.trim();
    row.unitOptions = mergeAllUomOptions(validUoms);

    if (inputValue) {
      const matchingSensor = validUoms.find(
        (uom) => uom.sensorName.toLowerCase() === inputValue.toLowerCase()
      );
      row.selectedSensorType = matchingSensor ? matchingSensor.sensorName : 'Custom';
    } else {
      row.selectedSensorType = '';
    }
  }

  updated[index] = row;
  return updated;
}

export function applyParameterUnitsChange(
  parameters: UIParameter[],
  index: number,
  units: string
): UIParameter[] {
  const updated = [...parameters];
  updated[index] = { ...updated[index], units };
  return updated;
}

export function mapToUIParameters(
  params: AssetParameter[],
  validUoms: SensorUomResponse[] = [],
  preselectDefaults: boolean = false
): UIParameter[] {
  return params.map((param) => {
    let unitOptions = Array.isArray(param.units) ? param.units : [];

    if (param.isUserConfigurable === true && validUoms.length > 0) {
      unitOptions = mergeAllUomOptions(validUoms);
    }

    const defaultUnit =
      preselectDefaults && param.hasUom && param.isUserConfigurable !== true && unitOptions.length > 0
        ? unitOptions[0].code
        : '';

    return {
      tag: param.tag,
      sensor: param.sensorName,
      position: param.position || '',
      businessFriendlyName: '',
      value: '',
      units: defaultUnit,
      unitOptions,
      originalUnits: Array.isArray(param.units) ? param.units : [],
      isUserConfigurable: param.isUserConfigurable,
      selectedSensorType: param.isUserConfigurable === true ? '' : undefined,
      hasUom: param.hasUom,
    };
  });
}

export function validateSimNumber(sim: string): string {
  const trimmedSim = (sim || '').trim();

  if (!trimmedSim) {
    return '';
  }
  if (/\s/.test(trimmedSim)) {
    return 'SIM number should not contain spaces';
  }
  if (!/^\d+$/.test(String(trimmedSim))) {
    return 'SIM number should contain only digits (0-9)';
  }
  if (trimmedSim.length < 5) {
    return 'SIM number should be at least 5 digits';
  }

  return '';
}

export function isActiveAssetStatus(status: string): boolean {
  return status === 'Enabled' || status === 'Active';
}

export function compareAssetsByActiveStatus(a: AssetDetails, b: AssetDetails): number {
  const aActive = isActiveAssetStatus(a.status);
  const bActive = isActiveAssetStatus(b.status);
  if (aActive === bActive) {
    return 0;
  }
  if (aActive) {
    return -1;
  }
  return 1;
}

export function plantHasSavedAssets(plantData: PlantData | null): boolean {
  return (plantData?.assets?.length ?? 0) > 0;
}

export function getDownloadConfigTitle(hasSelectedAsset: boolean): string {
  return hasSelectedAsset
    ? 'Download configuration files'
    : 'Select an asset to download config files';
}

export function getSaveAssetTitle(saveDisabled: boolean): string {
  return saveDisabled ? 'No changes to save' : 'Save asset';
}

export function parameterRowKey(param: UIParameter): string {
  return `${param.tag}-${param.sensor}-${param.position}`;
}

export function detectAssetFormChanges(
  originalAssetData: OriginalAssetFormSnapshot | null,
  selectedAssetFromHierarchy: AssetDetails | null,
  current: OriginalAssetFormSnapshot
): boolean {
  if (!originalAssetData || !selectedAssetFromHierarchy) {
    return true;
  }

  if (
    originalAssetData.selectedAsset !== current.selectedAsset ||
    originalAssetData.assetName !== current.assetName ||
    originalAssetData.assetStatus !== current.assetStatus ||
    originalAssetData.gatewayDeviceType !== current.gatewayDeviceType ||
    originalAssetData.gatewayDeviceId !== current.gatewayDeviceId ||
    originalAssetData.simNumber !== current.simNumber ||
    originalAssetData.controllerType !== current.controllerType ||
    originalAssetData.controllerDeviceId !== current.controllerDeviceId
  ) {
    return true;
  }

  if (originalAssetData.parameters.length !== current.parameters.length) {
    return true;
  }

  for (let i = 0; i < current.parameters.length; i++) {
    const currentParam = current.parameters[i];
    const originalParam = originalAssetData.parameters[i];

    if (
      (currentParam.businessFriendlyName || '') !== (originalParam.businessFriendlyName || '') ||
      (currentParam.units || '') !== (originalParam.units || '') ||
      (currentParam.selectedSensorType || '') !== (originalParam.selectedSensorType || '')
    ) {
      return true;
    }
  }

  return false;
}

function collectValidUomCodes(validUoms: SensorUomResponse[]): Set<string> {
  const codes = new Set<string>();
  validUoms.forEach((sensorUom) => {
    sensorUom.uoms.forEach((uom) => codes.add(uom.code));
  });
  return codes;
}

function getCustomProbeUomValidationIssue(
  param: UIParameter,
  validUnitCodes: Set<string>
): string | null {
  if (!param.businessFriendlyName.trim()) {
    return null;
  }
  if (!param.units) {
    return `${param.sensor} (no unit selected)`;
  }
  if (!validUnitCodes.has(param.units)) {
    return `${param.sensor} (invalid unit: ${param.units})`;
  }
  return null;
}

export function validateCustomProbeUoms(
  assetType: AssetType,
  parameters: UIParameter[],
  validUoms: SensorUomResponse[]
): { isValid: boolean; invalidSensors: string[] } {
  const validUnitCodes = collectValidUomCodes(validUoms);
  const invalidSensors: string[] = [];

  for (const param of parameters) {
    const assetTypeParam = assetType.parameters.find((atp) => atp.sensorName === param.sensor);
    if (assetTypeParam?.isUserConfigurable !== true) {
      continue;
    }

    const issue = getCustomProbeUomValidationIssue(param, validUnitCodes);
    if (issue) {
      invalidSensors.push(issue);
    }
  }

  return {
    isValid: invalidSensors.length === 0,
    invalidSensors,
  };
}

export function buildMissingFieldsErrorMessage(missingFields: string[]): string {
  if (missingFields.length === 1) {
    return `${missingFields[0]} is required`;
  }
  if (missingFields.length === 2) {
    return `${missingFields[0]} and ${missingFields[1]} are not provided`;
  }
  if (missingFields.length === 3) {
    return 'Asset Name, Gateway Serial Number, and SIM Number are not provided';
  }
  if (missingFields.length >= 4) {
    return 'Asset Name, Gateway Serial Number, SIM Number, Controller Serial Number are not provided';
  }
  return '';
}
